<template lang="html">
    <div class="risk-mgmt-notes notes-section">
        <p class="no-top-margin">
            Water volumes presented as "Potential Allocations" within this report are determined in consideration of
            the Province of BC's <i>Environmental Flow Needs Policy</i><sup>
                <footnote-tooltip
                    :footnote-number="7"
                />
            </sup>. Within the Policy, risk management measures are suggested to assess or mitigate potential effects
            of withdrawals from a stream, and provide an ecosystem perspective on environmental flow needs. The
            measures are associated with risk levels 1, 2, and 3 and are intended to guide where more caution may be
            needed in reviewing an application or making a decision.
        </p>

        <p>
            Where there are known species or habitat sensitivities, more detailed, site-specific studies may be
            required. Where detailed assessments or studies exist, they will supersede policy recommendations.
        </p>

        <p>
            Risk management levels, for assessing new applications to withdraw water, are determined for each month
            using the relationship of mean monthly flows to the mean annual discharge, and also using a stream size
            threshold based on mean annual flows. The calculations presented within this report assume all streams are
            fish-bearing. Where no water is indicated as available under a risk level, the stream may be very flow
            sensitive during that time, or the stream may have existing allocations in excess of the relevant
            threshold.
        </p>

        <p>
            Inter-annual hydrologic variability may affect the amount of water available in a given year. The impact of
            this variability on water allocations should be considered separately from the information presented in
            this report.
        </p>

        <p>
            The following risk management measures may be appropriate for consideration before a decision is made,
            could be completed by regional staff to inform a decision, or could be a condition of the licence or
            approval.
        </p>

        <p>
            Risk management measures may differ for short-term approvals vs. licences and may vary in relation to
            withdrawal amounts.
        </p>

        <div class="risk-mgmt-levels no-page-break">
            <div class="mgmt-lvl1 mgmt-lvl">
                <h2>
                    Risk Level 1:
                </h2>
                <div class="mgmt-lvl-content">
                    <p class="intro">
                        Supplemental measures to assess potential effects on environmental flow needs:
                    </p>

                    <ul>
                        <li>
                            Review relevant available information related to the WOI (e.g. water and fish reports on <a
                                target="_blank"
                                href="https://a100.gov.bc.ca/pub/acat/public/welcome.do"
                            >EcoCat</a>) and summarize it for the decision maker
                        </li>
                        <li>
                            Consider potential impacts of proposed diversion and use on downstream values (e.g.,
                            authorized water users, riparian owners, species and habitats)
                        </li>
                    </ul>
                </div>
            </div>
            <div class="mgmt-lvl2 mgmt-lvl">
                <h2>
                    Risk Level 2:
                </h2>
                <div class="mgmt-lvl-content">
                    <p class="intro">
                        In addition to Level 1 measures:
                    </p>
                    <p class="intro">
                        Supplemental measures to assess potential effects on environmental flow needs:
                    </p>

                    <ul>
                        <li>
                            Establish adequate baseline hydrological data (i.e., data representing flow conditions
                            prior to water diversion and use proposed in the application)
                        </li>
                        <li>
                            Prepare reconnaissance-level fish and fish habitat impact assessment (e.g., <a
                                target="_blank"
                                href="https://www2.gov.bc.ca/assets/gov/environment/natural-resource-stewardship/nr-laws-policy/risc/recce2c.pdf"
                            >RISC</a>, 2001)
                        </li>
                        <li>
                            Conduct an audit of actual water use within the basin use or a beneficial use review
                        </li>
                    </ul>

                    <p>
                        Examples of authorization terms and conditions that may mitigate potential negative effects
                        on environmental flow needs:
                    </p>
                    <ul>
                        <li>
                            Issue seasonal license, or restrict diversion and use during low flow or other
                            relevant period(s)
                        </li>
                        <li>
                            Require authorization-holder to develop storage
                        </li>
                        <li>
                            Include a daily maximum quantity or instantaneous withdrawal rate, e.g., greater
                            consideration of instantaneous demand over averages
                        </li>
                        <li>
                            Limit pump intake size
                        </li>
                        <li>
                            Require authorization-holder to monitor (e.g., install flow gauge) and report water
                            use during specific periods
                        </li>
                        <li>
                            Require authorization-holder to monitor flows and limit withdrawals when flows drop below
                            a certain level
                        </li>
                    </ul>
                </div>
            </div>
            <div class="mgmt-lvl3 mgmt-lvl">
                <h2>
                    Risk Level 3:
                </h2>
                <div class="mgmt-lvl-content">
                    <p class="intro">
                        In addition to Level 1 and Level 2 measures:
                    </p>
                    <p class="intro">
                        Supplemental measures to assess the potential effects on environmental flow needs:
                    </p>

                    <ul>
                        <li>
                            Prepare a detailed habitat assessment (e.g., <a
                                target="_blank"
                                href="http://www.env.gov.bc.ca/wld/documents/bmp/assessment_methods_instreamflow_in_bc.pdf"
                            >Lewis et al.</a> 2004; <a
                                target="_blank"
                                href="http://www.env.gov.bc.ca/wld/documents/bmp/guidelinesIFRv5_2.pdf"
                            >Hatfield et al.</a> 2007) Note that this includes development of a periodicity table.
                        </li>
                    </ul>

                    <p>
                        Examples of authorization terms and conditions that may mitigate potential negative effects
                        on environmental flow needs:
                    </p>
                    <ul>
                        <li>
                            Issues limited license terms (e.g., five years), allowing for review and potential
                            adjustment
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FootnoteTooltip from '@foundryspatial/footnote-tooltip';

export default {
    name: 'RiskMgmtSection',

    components: {
        FootnoteTooltip,
    },

    data: () => ({
    }),
};
</script>

<style lang="scss">
.watershed.web.report .risk-mgmt-notes {
    h2 {
        font-size: 1.8em;
    }

    .risk-mgmt-levels {
        margin-top: 1.25em;
        display: flex;
        flex-direction: column;

        .mgmt-lvl {
            $outer-corner-radius: 0.5em;

            flex: 1;
            padding: 1.25em;
            color: $white;

            p {
                margin: 0;

                &.intro {
                    font-weight: 700;
                }
            }

            &.mgmt-lvl1 {
                background: $risk-mgmt-level-1-color;
                border-top-left-radius: $outer-corner-radius;
                border-top-right-radius: $outer-corner-radius;
            }

            &.mgmt-lvl2 {
                background: $risk-mgmt-level-2-color;

                a {
                    color: #0035ff;
                }
            }

            &.mgmt-lvl3 {
                background: $risk-mgmt-level-3-color;
                border-bottom-left-radius: $outer-corner-radius;
                border-bottom-right-radius: $outer-corner-radius;
            }

            a:hover {
                color: inherit;
            }

            .mgmt-lvl-content {
                padding: 0.5em;

                p {
                    margin: 0.5em 0;
                }

                ul {
                    margin: 0.5em 0 0 3em;

                    @media screen and (max-width: $desktop) {
                        margin: 0.5em 1em;
                    }
                }
            }
        }
    }
}

.watershed.static.report .risk-mgmt-notes .risk-mgmt-levels {
    .mgmt-lvl {
        margin-bottom: 1em;

        // don't have bottom margin on the last child so that bottom spacing is handled by parent element
        &:last-child {
            margin-bottom: 0;
        }
    }

    h2 {
        font-weight: bold;
        margin-bottom: 1em;
    }
}
</style>
